import { FC, useMemo, useState } from 'react';
import SimpleModal from 'components/SimpleModal';
import DatePicker from 'components/DatePicker';
import DeactivationReasonSelect from 'components/DeactivationReasonSelect';
import { Box, TextField, Tooltip } from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { DeactivationReason } from '@constants/deactivation';
import useScheduleDeactivateTeams from 'hooks/useScheduleDeactivateTeams';

export interface ScheduleDeactivateTeamsModalProps {
  onClose: VoidFunction;
  onAfterSchedule?: VoidFunction;
  teamNamesById: Record<number, string>;
  teamTargets: Array<{ teamId: number; tenant: string; realmId: string }>;
}

export const ScheduleDeactivateTeamsModal: FC<ScheduleDeactivateTeamsModalProps> = ({
  onClose,
  onAfterSchedule,
  teamNamesById,
  teamTargets,
}) => {
  const { scheduleDeactivateTeams } = useScheduleDeactivateTeams();
  const [dataRetentionDays, setDataRetentionDays] = useState<number | undefined>();
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [deactivationReason, setDeactivationReason] = useState<DeactivationReason | ''>('');

  const isRetentionInvalid = useMemo(() => {
    return (
      dataRetentionDays !== undefined &&
      (!Number.isInteger(dataRetentionDays) || dataRetentionDays <= 0)
    );
  }, [dataRetentionDays]);

  const isDateInvalid = useMemo(() => {
    if (!scheduledDate) return true;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(scheduledDate)) return true;
    const [y, m, d] = scheduledDate.split('-').map((v) => Number(v));
    const candidate = new Date(y, (m || 1) - 1, d || 1);
    const isValid =
      candidate.getFullYear() === y &&
      candidate.getMonth() === (m || 1) - 1 &&
      candidate.getDate() === (d || 1);
    if (!isValid) return true;
    // Compare against start of today in local time; past dates invalid
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    candidate.setHours(0, 0, 0, 0);
    return candidate < today;
  }, [scheduledDate]);

  const minSelectableDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);
  const isConfirmDisabled = isRetentionInvalid || isDateInvalid || !deactivationReason;

  return (
    <SimpleModal
      open
      onClose={onClose}
      onConfirm={() => {
        if (!deactivationReason) {
          return;
        }
        scheduleDeactivateTeams({
          dataRetentionDays,
          deactivationReason,
          teamTargets,
          scheduledArchiveDate: scheduledDate,
          onSuccess: () => {
            onAfterSchedule?.();
          },
        });
        onClose();
      }}
      headerText="Teams Deactivation"
      confirmButtonText="Schedule Deactivation"
      disableConfirmButton={isConfirmDisabled}
      Body={
        <div>
          <div>You are about to schedule deactivation of:</div>
          <Box
            component="ul"
            sx={{
              maxHeight: 350,
              overflow: 'auto',
            }}
          >
            {teamTargets.map((t) => {
              const label = teamNamesById[t.teamId] ?? t.teamId;
              const key = `${t.teamId}-${t.tenant}-${t.realmId}`;
              return <li key={key}>{`${label}`}</li>;
            })}
          </Box>
          <Box
            sx={{
              mt: 2,
              mb: 1,
            }}
          >
            <div>This action will result in the team(s) being deactivated on:</div>
            <Box
              sx={{
                mt: 1,
              }}
            >
              <DatePicker
                label="Scheduled Deactivation Date"
                fullWidth
                value={scheduledDate}
                onChange={(newDate) => setScheduledDate(newDate ?? '')}
                minDate={minSelectableDate}
              />
            </Box>
          </Box>
          <Box
            sx={{
              mt: 2,
            }}
          >
            <DeactivationReasonSelect value={deactivationReason} onChange={setDeactivationReason} />
          </Box>
          <Box
            sx={{
              mt: 2,
            }}
          >
            <TextField
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      mr: 0.5,
                    }}
                  >
                    Custom Data Retention Period (days)
                  </Box>
                  <Tooltip
                    title={
                      'The time between "team get deactivated" and "team\'s data get hard-deleted"'
                    }
                  >
                    <InfoIcon style={{ fontSize: 16, color: '#6b7280' }} />
                  </Tooltip>
                </Box>
              }
              type="number"
              fullWidth
              value={dataRetentionDays ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setDataRetentionDays(value === '' ? undefined : Number(value));
              }}
              placeholder="Optional. Will use 180 days as default if not provided"
              slotProps={{
                htmlInput: { min: '1', step: '1' },
                inputLabel: { shrink: true },
              }}
            />
          </Box>
        </div>
      }
    />
  );
};

export default ScheduleDeactivateTeamsModal;
